import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  ArrowLeft,
  Save,
  FileText,
  Loader2,
  Menu,
  Hash,
  Search,
  UploadCloud,
  X,
} from "lucide-react";

import {
  useNavigate,useLocation,
  useParams,
} from "react-router-dom";

import adminApi, {
  STATIC_ORIGIN,
} from "./adminApi";

// import { CKEditor } from "@ckeditor/ckeditor5-react";
// import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Underline,
  Font,
  Alignment,
  List,
  Link,
  BlockQuote,
  Table as CKTable,
  TableToolbar,
  Image as CKImage,
  ImageUpload,
  ImageToolbar,
  ImageStyle,
  ImageResize,
  SimpleUploadAdapter,
  HorizontalLine,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";

const SEQUENCES = Array.from(
  { length: 31 },
  (_, i) => i + 1
);


const DISPLAY_CHECKBOXES = [
  {
    value: 16,
    label: "Quick Links",
  },
  {
    value: 15,
    label: "Top Menu",
  },
];

const emptyForm = {
  page_name: "",
  seq_id: "",
  display_parids: [],
  ischecked: false,
  page_title: "",
  page_contents: "",
  seo_metatag: "",
  seo_metatitle: "",
  seo_metadesc: "",
  seo_keyword: "",
  ischeckedpdf: false,
};

function attachmentUrl(filename) {
  if (!filename) return null;

  return `${STATIC_ORIGIN}/images/menudocuments/${filename}`;
}

export default function EditMainMenu() {
  const navigate = useNavigate();
  // const { id } = useParams();
  const location = useLocation();

  const id = location.state?.page_id;
  const [form, setForm] = useState(emptyForm);

  const [file, setFile] = useState(null);
  const [filePreviewName, setFilePreviewName] =
    useState("");

  const [existingAttachment, setExistingAttachment] =
    useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const fileInputRef = useRef(null);

  // =========================================================
  // INPUT STYLE
  // =========================================================

  const inputClass =
    "w-full h-[44px] rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 outline-none transition-all placeholder:text-slate-400 hover:border-slate-300 focus:border-[#0EA5A4] focus:ring-4 focus:ring-[#0EA5A4]/10";

  const textareaClass =
    "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-700 outline-none transition-all placeholder:text-slate-400 hover:border-slate-300 focus:border-[#0EA5A4] focus:ring-4 focus:ring-[#0EA5A4]/10 resize-none";

  // =========================================================
  // UPDATE FORM
  // =========================================================

  const updateForm = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // =========================================================
  // LOAD MAIN MENU
  // =========================================================

  const loadMenu = async () => {
    setLoading(true);
    setFormError("");

    try {
      const res = await adminApi.get(
        `/admin/main-menu/${id}`
      );

      const d = res.data.item;

      setForm({
        page_name: d.page_name || "",

        seq_id: d.seq_id || "",

        display_parids: Array.isArray(
          d.display_parids
        )
          ? d.display_parids
          : [],

        ischecked: !!d.ischecked,

        page_title: d.page_title || "",

        page_contents: d.page_contents || "",

        seo_metatag:
          d.seo?.seo_metatag || "",

        seo_metatitle:
          d.seo?.seo_metatitle || "",

        seo_metadesc:
          d.seo?.seo_metadesc || "",

        seo_keyword:
          d.seo?.seo_keyword || "",

        ischeckedpdf: !!d.ischeckedpdf,
      });

      setExistingAttachment(
        d.attachment || null
      );

    } catch (err) {
      console.error(err);

      setFormError(
        err.response?.data?.message ||
          "Could not load this record."
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    if (id) {
      loadMenu();
    }
  }, [id]);

  // =========================================================
  // DISPLAY CHECKBOX
  // =========================================================

  const toggleDisplayParid = (value) => {
    setForm((prev) => ({
      ...prev,

      display_parids:
        prev.display_parids.includes(value)
          ? prev.display_parids.filter(
              (v) => v !== value
            )
          : [
              ...prev.display_parids,
              value,
            ],
    }));
  };

  // =========================================================
  // FILE CHANGE
  // =========================================================

  const handleFileChange = (e) => {
    const selectedFile =
      e.target.files?.[0];

    if (!selectedFile) return;

    if (
      !selectedFile.name
        .toLowerCase()
        .endsWith(".pdf")
    ) {
      setFormError(
        "Only PDF files are allowed."
      );

      e.target.value = "";
      return;
    }

    if (
      selectedFile.size >
      4 * 1024 * 1024
    ) {
      setFormError(
        "PDF file size must be less than 4MB."
      );

      e.target.value = "";
      return;
    }

    setFormError("");

    setFile(selectedFile);
    setFilePreviewName(
      selectedFile.name
    );
  };

  // =========================================================
  // REMOVE NEW FILE
  // =========================================================

  const removeFile = () => {
    setFile(null);
    setFilePreviewName("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setFormError("");

    // -------------------------------------------------------
    // VALIDATION
    // -------------------------------------------------------

    if (!form.page_name.trim()) {
      setFormError(
        "Main Menu Name is required."
      );
      return;
    }

    if (!form.seq_id) {
      setFormError(
        "Sequence is required."
      );
      return;
    }

    if (
      form.ischecked &&
      (
        !form.page_title.trim() ||
        !form.page_contents.trim()
      )
    ) {
      setFormError(
        "Title and Contents are required when 'Add content for this menu' is checked."
      );
      return;
    }

    if (
      form.ischeckedpdf &&
      !file &&
      !existingAttachment
    ) {
      setFormError(
        "A PDF file is required when 'Add PDF for this menu' is checked."
      );
      return;
    }

    // -------------------------------------------------------
    // FORM DATA
    // -------------------------------------------------------

    const fd = new FormData();

    fd.append(
      "page_name",
      form.page_name.trim()
    );

    fd.append(
      "seq_id",
      form.seq_id
    );

    fd.append(
      "display_parids",
      JSON.stringify(
        form.display_parids
      )
    );

    fd.append(
      "ischecked",
      form.ischecked ? "1" : "0"
    );

    fd.append(
      "page_title",
      form.page_title.trim()
    );

    fd.append(
      "page_contents",
      form.page_contents.trim()
    );

    fd.append(
      "seo_metatag",
      form.seo_metatag
    );

    fd.append(
      "seo_metatitle",
      form.seo_metatitle
    );

    fd.append(
      "seo_metadesc",
      form.seo_metadesc
    );

    fd.append(
      "seo_keyword",
      form.seo_keyword
    );

    fd.append(
      "ischeckedpdf",
      form.ischeckedpdf ? "1" : "0"
    );

    if (file) {
      fd.append(
        "attachment",
        file
      );
    }

    // -------------------------------------------------------
    // SAVE
    // -------------------------------------------------------

    setSaving(true);

    try {
      await adminApi.put(
        `/admin/main-menu/${id}`,
        fd
      );

      navigate(
        "/admin/contents/main-menu",
        {
          state: {
            type: "success",
            message:
              "Main menu content updated successfully.",
          },
        }
      );

    } catch (err) {
      console.error(err);

      setFormError(
        err.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div className="min-h-full bg-[#f5f7fa]">

        <div className="max-w-[1400px] mx-auto">

          <div className="bg-white border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)]">

            <div
              className="px-2"
              style={{
                paddingTop: "10px",
                paddingBottom: "10px",
              }}
            >

              <div className="flex items-center gap-3">

                <Loader2
                  size={18}
                  className="animate-spin text-[#0EA5A4]"
                />

                <span className="text-sm text-slate-400">
                  Loading menu content...
                </span>

              </div>

            </div>

          </div>

        </div>

      </div>
    );
  }

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="min-h-full bg-[#f5f7fa]">

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <div className="mb-3">

        <div className="max-w-[1400px] mx-auto">

          <div className="bg-white border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)]">

            <div
              className="px-2 sm:px-2"
              style={{
                paddingTop: "10px",
                paddingBottom: "10px",
              }}
            >

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

                {/* TITLE */}

                <div className="flex items-center gap-3">

                  <div>

                    <h1 className="font-display text-[18px] sm:text-[18px] font-bold text-slate-800">
                      Edit Main Menu Content
                    </h1>

                    <p className="text-xs sm:text-sm text-slate-400 mt-0.5 d-none">
                      Update and manage your main menu content page
                    </p>

                  </div>

                </div>

                {/* BACK */}

                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      "/admin/contents/main-menu"
                    )
                  }
                  className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition"
                >

                  <ArrowLeft size={16} />

                  Back

                </button>

              </div>

            </div>

          </div>

        </div>

      </div>

      {/* =====================================================
          FORM
      ===================================================== */}

      <div>

        <div className="max-w-[1400px] mx-auto">

          <form
            onSubmit={handleSubmit}
            className="space-y-5"
          >

            {/* =================================================
                BASIC INFORMATION
            ================================================= */}

            <div className="bg-white border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)] overflow-hidden">

              {/* CARD HEADER */}

              <div className="px-4 sm:px-7 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white d-none">

                <div className="flex items-center gap-3">

                  <div className="w-9 h-9 rounded-lg bg-[#0B2545]/10 flex items-center justify-center">

                    <Menu
                      size={17}
                      className="text-[#0B2545]"
                    />

                  </div>

                  <div>

                    <h2 className="text-sm font-bold text-slate-700">
                      Menu Information
                    </h2>

                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Configure main menu details
                    </p>

                  </div>

                </div>

              </div>

              {/* CARD BODY */}

              <div className="p-1 sm:p-7">

                <table
                  className="table table-bordered"
                  style={{
                    marginBottom: "1px",
                  }}
                >

                  <tbody>

                    {/* MAIN MENU + SEQUENCE */}

                    <tr>

                      <td style={{ width: "10%" }}>

                       <label className="block text-xs font-semibold text-slate-600 labelStly">
                          Main Menu Name <span className="text-red-500">*</span>
                        </label>

                      </td>

                      <td style={{ width: "40%" }}>

                        <div className="relative">

                          <Menu
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                          />

                          <input
                            type="text"
                            value={
                              form.page_name
                            }
                            onChange={(e) =>
                              updateForm(
                                "page_name",
                                e.target.value
                              )
                            }
                            placeholder="Enter main menu name"
                            className={`${inputClass} pl-9`}
                          />

                        </div>

                      </td>

                      <td style={{ width: "10%" }}>

                       <label className="block text-xs font-semibold text-slate-600 labelStly">
                          Sequence <span className="text-red-500">*</span>
                        </label>

                      </td>

                      <td>

                        <div className="relative">

                          <Hash
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                          />

                          <select
                            value={
                              form.seq_id
                            }
                            onChange={(e) =>
                              updateForm(
                                "seq_id",
                                e.target.value
                              )
                            }
                            className={`${inputClass} pl-9 appearance-none cursor-pointer`}
                          >

                            <option value="">
                              -- Select Sequence --
                            </option>

                            {SEQUENCES.map(
                              (sequence) => (
                                <option
                                  key={
                                    sequence
                                  }
                                  value={
                                    sequence
                                  }
                                >
                                  {
                                    sequence
                                  }
                                </option>
                              )
                            )}

                            {/*
                                If this record's sequence is greater than 31
                                (e.g. it was the last +1 when created),
                                show it as an additional option so it stays selected.
                            */}
                            {Number(form.seq_id) > 31 && (

                              <option
                                value={form.seq_id}
                              >
                                {form.seq_id}
                              </option>

                            )}

                          </select>

                        </div>

                      </td>

                    </tr>

                    {/* DISPLAY MENU */}

                    <tr>

                      <td style={{ width: "10%" }}>

                       <label className="block text-xs font-semibold text-slate-600 labelStly">
                          Display Menu Link at <span className="text-red-500">*</span>
                        </label>

                      </td>

                      <td colSpan={3}>

                        <div className="flex items-center gap-6 min-h-[44px]">

                          {DISPLAY_CHECKBOXES.map(
                            (c) => (

                              <label
                                key={
                                  c.value
                                }
                                className="flex items-center gap-2 cursor-pointer select-none"
                              >

                                <input
                                  type="checkbox"
                                  checked={form.display_parids.includes(
                                    c.value
                                  )}
                                  onChange={() =>
                                    toggleDisplayParid(
                                      c.value
                                    )
                                  }
                                  className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                                />

                                <span className="text-sm text-slate-600">
                                  {
                                    c.label
                                  }
                                </span>

                              </label>

                            )
                          )}

                        </div>

                      </td>

                    </tr>

                    {/* CONTENT CHECKBOX */}

                    <tr>

                      <td style={{ width: "10%" }}>

                        <label className="block text-xs font-semibold text-slate-600 labelStly">
                          Menu Content
                        </label>

                      </td>

                      <td colSpan={3}>

                        <label className="flex items-center gap-2.5 cursor-pointer select-none min-h-[44px]">

                          <input
                            type="checkbox"
                            checked={
                              form.ischecked
                            }
                            onChange={(e) =>
                              updateForm(
                                "ischecked",
                                e.target.checked
                              )
                            }
                            className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                          />

                          <span className="text-sm font-medium text-slate-700">
                            Add content for this menu
                          </span>

                        </label>

                      </td>

                    </tr>

                  </tbody>

                </table>

              </div>

            </div>

            {/* =================================================
                PAGE CONTENT CARD
            ================================================= */}

            {form.ischecked && (

              <div className="bg-white border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)] overflow-hidden">

                {/* CARD HEADER */}

                <div className="px-4 sm:px-7 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white">

                  <div className="flex items-center gap-3">

                    <div className="w-9 h-9 rounded-lg bg-[#0EA5A4]/10 flex items-center justify-center">

                      <FileText
                        size={17}
                        className="text-[#0EA5A4]"
                      />

                    </div>

                    <div>

                      <h2 className="text-sm font-bold text-slate-700">
                        Page Content
                      </h2>

                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Update the content that will appear on this page
                      </p>

                    </div>

                  </div>

                </div>

                {/* CARD BODY */}

                <div
                  className="p-4 sm:p-7"
                  style={{
                    marginTop: "-30px",
                  }}
                >

                  <table
                    className="table table-bordered"
                    style={{
                      marginBottom: "1px",
                    }}
                  >

                    <tbody>

                      {/* PAGE TITLE */}

                      <tr>

                        <td style={{ width: "10%" }}>

                          <label className="block text-xs font-semibold text-slate-600 labelStly">
                            Page Title
                          </label>

                        </td>

                        <td>

                          <div className="relative">

                            <input
                              type="text"
                              value={
                                form.page_title
                              }
                              onChange={(e) =>
                                updateForm(
                                  "page_title",
                                  e.target.value
                                )
                              }
                              placeholder="Enter page title"
                              className={
                                inputClass
                              }
                            />

                          </div>

                        </td>

                      </tr>

                      {/* PAGE CONTENT */}

                      <tr>

                        <td style={{ width: "10%" }}>

                          <label className="block text-xs font-semibold text-slate-600 labelStly">
                            Page Contents
                          </label>

                        </td>

                        <td>

                          <div className="rounded-xl overflow-hidden border border-slate-200">

                            {/* <CKEditor
                              editor={
                                ClassicEditor
                              }
                              data={
                                form.page_contents
                              }
                              onChange={(
                                event,
                                editor
                              ) => {

                                const data =
                                  editor.getData();

                                setForm(
                                  (prev) => ({
                                    ...prev,
                                    page_contents:
                                      data,
                                  })
                                );

                              }}
                            /> */}

                             <CKEditor
                                editor={ClassicEditor}
                                data={form.page_contents}
                                config={{
                                  licenseKey: "GPL",
                                  plugins: [
                                    Essentials, Paragraph, Heading,
                                    Bold, Italic, Underline,
                                    Font, Alignment,
                                    List, Link, BlockQuote,
                                    CKTable, TableToolbar,
                                    CKImage, ImageUpload, ImageToolbar, ImageStyle, ImageResize,
                                    SimpleUploadAdapter,
                                    HorizontalLine,
                                  ],
                                  toolbar: [
                                    "heading", "|",
                                    "fontFamily", "fontSize", "|",
                                    "bold", "italic", "underline", "|",
                                    "fontColor", "fontBackgroundColor", "|",
                                    "alignment", "|",
                                    "bulletedList", "numberedList", "|",
                                    "link", "blockQuote", "insertTable", "uploadImage", "horizontalLine", "|",
                                    "undo", "redo",
                                  ],
                                  fontFamily: {
                                    options: [
                                      "default",
                                      "Arial, Helvetica, sans-serif",
                                      "Georgia, serif",
                                      "Times New Roman, Times, serif",
                                      "Courier New, Courier, monospace",
                                      "Verdana, Geneva, sans-serif",
                                      "Tahoma, Geneva, sans-serif",
                                      "Trebuchet MS, Helvetica, sans-serif",
                                    ],
                                    supportAllValues: true,
                                  },
                                  fontSize: {
                                    options: [9, 11, 13, "default", 17, 19, 21, 24, 28, 32],
                                    supportAllValues: true,
                                  },
                                  table: {
                                    contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
                                  },
                                  image: {
                                    toolbar: [
                                      "imageStyle:alignLeft",
                                      "imageStyle:alignRight",
                                      "imageStyle:block",
                                      "|",
                                      "toggleImageCaption",
                                      "imageTextAlternative",
                                      "resizeImage",
                                    ],
                                  },
                                  simpleUpload: {
                                    uploadUrl: `${adminApi.defaults.baseURL}/admin/main-menu/upload-image`,
                                    headers: {
                                      Authorization: `Bearer ${localStorage.getItem("mkcg_admin_token")}`,
                                    },
                                  },
                                }}
                                onChange={(event, editor) => {
                                  const data = editor.getData();
                                  setForm((prev) => ({
                                    ...prev,
                                    page_contents: data,
                                  }));
                                }}
                              />                         

                          </div>

                        </td>

                      </tr>

                    </tbody>

                  </table>

                  {/* =================================================
                      SEO TITLE
                  ================================================= */}

                  <div className="mt-4">

                    <div className="flex items-center gap-3 mb-4">

                      <div className="h-px bg-slate-200 flex-1" />

                      <div className="flex items-center gap-2 px-3">

                        <Search
                          size={15}
                          className="text-[#0EA5A4]"
                        />

                        <span className="text-xs font-bold uppercase tracking-wider text-[#0EA5A4]">
                          SEO Details
                        </span>

                      </div>

                      <div className="h-px bg-slate-200 flex-1" />

                    </div>

                  </div>

                  {/* =================================================
                      SEO TABLE
                  ================================================= */}

                  <table
                    className="table table-bordered"
                    style={{
                      marginBottom: "1px",
                    }}
                  >

                    <tbody>

                      {/* META TAGS + META TITLE */}

                      <tr>

                        <td style={{ width: "10%" }}>

                          <label className="block text-xs font-semibold text-slate-600 labelStly">
                            Meta Tags
                          </label>

                        </td>

                        <td>

                          <textarea
                            rows={3}
                            value={
                              form.seo_metatag
                            }
                            onChange={(e) =>
                              updateForm(
                                "seo_metatag",
                                e.target.value
                              )
                            }
                            placeholder="Enter meta tags"
                            className={
                              textareaClass
                            }
                          />

                        </td>

                        <td style={{ width: "10%" }}>

                          <label className="block text-xs font-semibold text-slate-600 labelStly">
                            Meta Title
                          </label>

                        </td>

                        <td>

                          <textarea
                            rows={3}
                            value={
                              form.seo_metatitle
                            }
                            onChange={(e) =>
                              updateForm(
                                "seo_metatitle",
                                e.target.value
                              )
                            }
                            placeholder="Enter meta title"
                            className={
                              textareaClass
                            }
                          />

                        </td>

                      </tr>

                      {/* META DESCRIPTION + KEYWORDS */}

                      <tr>

                        <td style={{ width: "10%" }}>

                          <label className="block text-xs font-semibold text-slate-600 labelStly">
                            Meta Description
                          </label>

                        </td>

                        <td>

                          <textarea
                            rows={3}
                            value={
                              form.seo_metadesc
                            }
                            onChange={(e) =>
                              updateForm(
                                "seo_metadesc",
                                e.target.value
                              )
                            }
                            placeholder="Enter meta description"
                            className={
                              textareaClass
                            }
                          />

                        </td>

                        <td style={{ width: "10%" }}>

                          <label className="block text-xs font-semibold text-slate-600 labelStly">
                            Keywords
                          </label>

                        </td>

                        <td>

                          <textarea
                            rows={3}
                            value={
                              form.seo_keyword
                            }
                            onChange={(e) =>
                              updateForm(
                                "seo_keyword",
                                e.target.value
                              )
                            }
                            placeholder="Enter keywords separated by commas"
                            className={
                              textareaClass
                            }
                          />

                        </td>

                      </tr>

                    </tbody>

                  </table>

                </div>

              </div>

            )}

            {/* =================================================
                PDF CARD
            ================================================= */}

            <div className="bg-white border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)] overflow-hidden">

              {/* CARD HEADER */}

              <div className="px-4 sm:px-7 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white">

                <div className="flex items-center gap-3">

                  <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">

                    <FileText
                      size={17}
                      className="text-red-500"
                    />

                  </div>

                  <div className="flex items-center justify-between w-full">

                    <div>

                      <h2 className="text-sm font-bold text-slate-700">
                        PDF Attachment
                      </h2>

                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Optionally attach a PDF document to this menu
                      </p>

                    </div>

                    {/* PDF TOGGLE */}

                    <label className="flex items-center gap-2.5 cursor-pointer select-none">

                      <span className="hidden sm:block text-xs font-medium text-slate-500">
                        Enable PDF
                      </span>

                      <div className="relative">

                        <input
                          type="checkbox"
                          checked={
                            form.ischeckedpdf
                          }
                          onChange={(e) =>
                            updateForm(
                              "ischeckedpdf",
                              e.target.checked
                            )
                          }
                          className="sr-only peer"
                        />

                        <div
                          className="
                            w-10 h-5
                            bg-slate-200
                            rounded-full
                            peer
                            peer-checked:bg-[#0EA5A4]
                            transition
                          "
                        />

                        <div
                          className="
                            absolute
                            left-0.5 top-0.5
                            w-4 h-4
                            bg-white
                            rounded-full
                            shadow-sm
                            transition-transform
                            peer-checked:translate-x-5
                          "
                        />

                      </div>

                    </label>

                  </div>

                </div>

              </div>

              {/* PDF BODY */}

              <div
                className=""
                style={{
                  paddingLeft: "20px",
                  paddingRight: "20px",
                }}
              >

                {form.ischeckedpdf && (

                  <div className="mb-3">

                    <label className="text-xs font-semibold text-slate-600 mb-2 block">

                      Upload PDF

                      <span className="text-red-500 ml-1">
                        *
                      </span>

                    </label>

                    {/* =================================================
                        NO NEW FILE
                    ================================================= */}

                    {!file ? (

                      <div>

                        <div
                          onClick={() =>
                            fileInputRef.current?.click()
                          }
                          className="group border-2 border-dashed border-slate-200 bg-slate-50/60 hover:bg-[#0EA5A4]/[0.03] hover:border-[#0EA5A4]/50 transition cursor-pointer p-4"
                        >

                          <div className="flex flex-col items-center justify-center text-center">

                            <div className="w-14 h-14 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mb-3 group-hover:border-[#0EA5A4]/30 transition">

                              <UploadCloud
                                size={24}
                                className="text-slate-400 group-hover:text-[#0EA5A4] transition"
                              />

                            </div>

                            <p className="text-sm font-semibold text-slate-600">

                              {existingAttachment
                                ? "Click to replace PDF"
                                : "Click to upload PDF"}

                            </p>

                            <p className="text-xs text-slate-400 mt-1">
                              PDF only · Maximum 4MB
                            </p>

                          </div>

                        </div>

                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,application/pdf"
                          onChange={
                            handleFileChange
                          }
                          className="hidden"
                        />

                      </div>

                    ) : (

                      /* =================================================
                         NEW FILE SELECTED
                      ================================================= */

                      <div className="flex items-center justify-between gap-4 border border-emerald-100 bg-emerald-50/60 rounded-2xl p-4">

                        <div className="flex items-center gap-3 min-w-0">

                          <div className="w-11 h-11 rounded-xl bg-white border border-emerald-100 flex items-center justify-center shrink-0">

                            <FileText
                              size={20}
                              className="text-red-500"
                            />

                          </div>

                          <div className="min-w-0">

                            <p className="text-sm font-semibold text-slate-700 truncate">

                              {
                                filePreviewName
                              }

                            </p>

                            <p className="text-[11px] text-emerald-600 mt-0.5">
                              New PDF selected successfully
                            </p>

                          </div>

                        </div>

                        <button
                          type="button"
                          onClick={
                            removeFile
                          }
                          className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-red-500 hover:border-red-200 transition shrink-0"
                        >

                          <X size={16} />

                        </button>

                      </div>

                    )}

                    {/* =================================================
                        EXISTING PDF
                    ================================================= */}

                    {existingAttachment && (

                      <div className="mt-3 flex items-center justify-between gap-3 border border-slate-200 rounded-xl bg-slate-50 px-4 py-3">

                        <div className="flex items-center gap-3 min-w-0">

                          <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">

                            <FileText
                              size={18}
                              className="text-red-500"
                            />

                          </div>

                          <div className="min-w-0">

                            <p className="text-xs font-semibold text-slate-600">
                              Existing PDF
                            </p>

                            <p className="text-[11px] text-slate-400 truncate">
                              {
                                existingAttachment
                              }
                            </p>

                          </div>

                        </div>

                        <a
                          href={attachmentUrl(
                            existingAttachment
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 inline-flex items-center justify-center h-9 px-4 rounded-lg bg-white border border-slate-200 text-xs font-medium text-[#0EA5A4] hover:border-[#0EA5A4] hover:bg-[#0EA5A4]/5 transition"
                        >
                          View PDF
                        </a>

                      </div>

                    )}

                  </div>

                )}

              </div>

            </div>

            {/* =================================================
                ERROR
            ================================================= */}

            {formError && (

              <div className="flex items-start gap-3 px-4 py-3.5 bg-red-50 border border-red-100 rounded-xl">

                <div className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs font-bold shrink-0">
                  !
                </div>

                <p className="text-sm text-red-600">
                  {formError}
                </p>

              </div>

            )}

            {/* =================================================
                FOOTER ACTION
            ================================================= */}

            <div className="bg-white border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)]">

              <div className="px-5 sm:px-7 py-4 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3">

                {/* CANCEL */}

                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      "/admin/contents/main-menu"
                    )
                  }
                  className="h-11 px-6 rounded-xl text-sm font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition"
                >
                  Cancel
                </button>

                {/* UPDATE */}

                <button
                  type="submit"
                  disabled={saving}
                  className="h-11 px-7 rounded-xl flex items-center justify-center gap-2 bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] hover:from-[#143A63] hover:to-[#0c9291] text-white text-sm font-semibold shadow-sm hover:shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed"
                >

                  {saving ? (
                    <Loader2
                      size={17}
                      className="animate-spin"
                    />
                  ) : (
                    <Save size={17} />
                  )}

                  {saving
                    ? "Updating..."
                    : "Update Content"}

                </button>

              </div>

            </div>

          </form>

        </div>

      </div>

    </div>
  );
}